import { useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:6200'

export default function InvitationAcceptanceFlow({ invitations, onCreateInstead }) {
  const { token, updateUser } = useAuth()
  const [loading, setLoading] = useState(null) // ID of invitation being accepted
  const [declining, setDeclining] = useState(null) // ID of invitation being declined
  const [error, setError] = useState('')

  const handleAccept = async (invitationId) => {
    setLoading(invitationId)
    setError('')

    try {
      const response = await fetch(`${API_URL}/api/v1/invitations/${invitationId}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to accept invitation')
      }

      // Update user context
      if (data.user) {
        updateUser(data.user)
      }
    } catch (err) {
      setError(err.message || 'Failed to accept invitation. Please try again.')
    } finally {
      setLoading(null)
    }
  }

  const handleDecline = async (invitationId) => {
    setDeclining(invitationId)
    setError('')

    try {
      const response = await fetch(`${API_URL}/api/v1/invitations/${invitationId}/decline`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.detail || 'Failed to decline invitation')
      }

      // Invitation declined - refresh the page or remove from list
      window.location.reload()
    } catch (err) {
      setError(err.message || 'Failed to decline invitation. Please try again.')
    } finally {
      setDeclining(null)
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
          <span className="material-symbols-outlined text-blue-600 text-4xl">mail</span>
        </div>
        <h3 className="text-[#111418] text-2xl font-bold mb-2">
          You've been invited!
        </h3>
        <p className="text-[#617589] text-base">
          Accept an invitation to join an organization
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="rounded-lg bg-red-50 p-4 border border-red-200">
          <div className="flex items-start">
            <span className="material-symbols-outlined text-red-600 mr-3 text-[20px]">error</span>
            <p className="text-sm font-medium text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Invitations List */}
      <div className="space-y-3">
        {invitations.map((invitation) => (
          <div
            key={invitation.id}
            className="p-5 border-2 border-[#dbe0e6] rounded-xl bg-white"
          >
            {/* Organization Info */}
            <div className="flex items-start gap-4 mb-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                invitation.tenancy.tenancy_type === 'CLIENT'
                  ? 'bg-blue-100'
                  : 'bg-purple-100'
              }`}>
                <span className={`material-symbols-outlined text-2xl ${
                  invitation.tenancy.tenancy_type === 'CLIENT'
                    ? 'text-blue-600'
                    : 'text-purple-600'
                }`}>
                  {invitation.tenancy.tenancy_type === 'CLIENT' ? 'business' : 'groups'}
                </span>
              </div>
              <div className="flex-1">
                <h4 className="text-[#111418] text-lg font-bold">{invitation.tenancy.name}</h4>
                <p className="text-[#617589] text-sm">
                  {invitation.tenancy.tenancy_type === 'CLIENT' ? 'Client Organization' : 'Service Provider'}
                </p>
              </div>
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${
                invitation.role === 'OWNER'
                  ? 'bg-purple-100 text-purple-700'
                  : invitation.role === 'ADMIN'
                  ? 'bg-blue-100 text-blue-700'
                  : invitation.role === 'MEMBER'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-700'
              }`}>
                {invitation.role}
              </span>
            </div>

            {/* Invitation Details */}
            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2 text-sm">
                <span className="material-symbols-outlined text-[#617589] text-[18px]">person</span>
                <span className="text-[#617589]">
                  Invited by <span className="font-semibold text-[#111418]">{invitation.invited_by.name || invitation.invited_by.email}</span>
                </span>
              </div>
              {invitation.message && (
                <div className="flex items-start gap-2 text-sm">
                  <span className="material-symbols-outlined text-[#617589] text-[18px]">chat</span>
                  <span className="text-[#617589] italic">"{invitation.message}"</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm">
                <span className="material-symbols-outlined text-[#617589] text-[18px]">schedule</span>
                <span className="text-[#617589]">
                  Expires {formatDate(invitation.expires_at)}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <button
                onClick={() => handleAccept(invitation.id)}
                disabled={loading === invitation.id || declining === invitation.id}
                className="flex-1 bg-primary hover:bg-primary/90 text-white font-bold rounded-lg h-10 text-sm shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading === invitation.id ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin material-symbols-outlined text-[18px]">progress_activity</span>
                    Accepting...
                  </span>
                ) : (
                  'Accept'
                )}
              </button>
              <button
                onClick={() => handleDecline(invitation.id)}
                disabled={loading === invitation.id || declining === invitation.id}
                className="flex-1 border-2 border-[#dbe0e6] text-[#111418] font-bold rounded-lg h-10 text-sm hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {declining === invitation.id ? 'Declining...' : 'Decline'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Divider */}
      <div className="relative flex items-center py-2">
        <div className="flex-grow border-t border-[#dbe0e6]"></div>
        <span className="flex-shrink-0 mx-4 text-sm text-[#617589]">or</span>
        <div className="flex-grow border-t border-[#dbe0e6]"></div>
      </div>

      {/* Create New Organization Option */}
      <button
        onClick={onCreateInstead}
        className="w-full p-4 border-2 border-dashed border-[#dbe0e6] rounded-xl hover:border-primary hover:bg-primary/5 transition-all text-center group"
      >
        <span className="material-symbols-outlined text-[#617589] group-hover:text-primary text-3xl mb-2">add_circle</span>
        <p className="text-[#111418] font-semibold text-sm">Create your own organization instead</p>
      </button>
    </div>
  )
}
